import * as React from 'react';
import { useCallback, useRef } from 'react';
import lowerCase from 'lodash/lowerCase';

import { CountBadge, Pluralize, Tooltip } from 'components/common';
import useEntityCollectionStatus from 'collections/hooks/useEntityCollectionStatus';
import type { ColumnRenderer } from 'components/common/EntityDataTable/types';
import { createGRN } from 'logic/permissions/GRN';
import useExpandedSections from 'components/common/EntityDataTable/hooks/useExpandedSections';

type Props = {
  entityId: string;
  entityType: string;
};

const EntityCollectionCell = ({ entityId, entityType }: Props) => {
  const entityGRN = createGRN(entityType, entityId);
  const { data, isFetching, isError } = useEntityCollectionStatus(entityGRN, entityId);
  const count = data?.length || 0;
  const isDisabled = count === 0;

  const buttonRef = useRef();
  const { toggleSection, expandedSections } = useExpandedSections();
  const collectionSectionIsOpen = expandedSections?.[entityId]?.includes('collections');

  const toggleCollectionSection = useCallback(() => toggleSection(entityId, 'collections'), [entityId, toggleSection]);

  if (isFetching || isError) {
    return null;
  }
  const label = (
    <span>
      This {lowerCase(entityType)} is in {count} <Pluralize value={count} plural="collections" singular="collection" />.
    </span>
  );

  return (
    <Tooltip withArrow position="right" label={label} disabled={isDisabled}>
      <CountBadge
        count={count}
        title={`${collectionSectionIsOpen ? 'Hide' : 'Show'} collections`}
        onClick={!isDisabled ? () => toggleCollectionSection() : undefined}
        ref={buttonRef}
      />
    </Tooltip>
  );
};

export const getCollectionsCustomColumnRenderer = (entityType: string): { collections: ColumnRenderer<any> } => ({
  collections: {
    renderCell: (_collections: boolean, entity: any) => (
      <EntityCollectionCell entityId={entity.id} entityType={entityType} />
    ),
    staticWidth: 130,
  },
});

export default EntityCollectionCell;
